<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import ExternalLink from 'lucide-vue-next/dist/esm/icons/external-link.js'; import Sparkles from 'lucide-vue-next/dist/esm/icons/sparkles.js';
import {
  NxBanner,
  NxChatComposer,
  NxChatMessage,
  NxChatThread,
  NxEmptyState,
  NxFloatingPanel,
  NxIconButton,
  NxSpinner,
} from '@blpde/nx-ui';
import { useShellAssistant } from '@/assistant/useShellAssistant';

const { t } = useI18n();
const router = useRouter();
const {
  open,
  booting,
  sending,
  error,
  agent,
  conversation,
  contextLine,
  messages,
  draft,
  stickKey,
  closeAssistant,
  send,
  cancel,
} = useShellAssistant();

/** Clear of the assistant button in the corner. */
const PANEL_MARGIN = { x: 20, y: 92 };
const PANEL_WIDTH = 'var(--nxt-panel-width-2xl)';
const PANEL_HEIGHT = 'min(calc(var(--nxt-size-unit) * 35), calc(100vh - var(--nxt-area-size-md)))';

const title = computed(() => t('connect.assistant.title'));
const subtitle = computed(() => {
  if (contextLine.value) return contextLine.value;
  if (agent.value?.name) return agent.value.name;
  return t('connect.assistant.subtitle');
});

/** The panel's own close button and Escape end the session like the app's button does. */
function onOpenChange(isOpen: boolean) {
  if (!isOpen) closeAssistant();
}

function onSend(content: string) {
  void send(content);
}

function openFullApp() {
  const id = conversation.value?.id;
  closeAssistant();
  if (id) {
    void router.push({
      name: 'agent.Chat.Conversation',
      params: { conversationId: String(id) },
    });
    return;
  }
  void router.push('/agent');
}
</script>

<template>
  <NxFloatingPanel
    :open="open"
    :title="title"
    :subtitle="subtitle"
    position-key="connect.assistant"
    :margin="PANEL_MARGIN"
    :width="PANEL_WIDTH"
    :height="PANEL_HEIGHT"
    :close-label="t('connect.assistant.close')"
    :close-on-escape="false"
    flush
    @update:open="onOpenChange"
  >
    <template #header-leading>
      <span class="cx-assistant__spark" aria-hidden="true">
        <Sparkles class="nxt-icon-md" />
      </span>
    </template>
    <template #header-actions>
      <NxIconButton
        height="xs"
        :title="t('connect.assistant.openFull')"
        :aria-label="t('connect.assistant.openFull')"
        @click="openFullApp"
      >
        <ExternalLink class="nxt-icon-md" />
      </NxIconButton>
    </template>

    <div v-if="booting" class="cx-assistant__state">
      <NxSpinner size="lg" />
      <span>{{ t('connect.assistant.booting') }}</span>
    </div>

    <div v-else-if="error === 'no-agent'" class="cx-assistant__state">
      {{ t('connect.assistant.noAgent') }}
    </div>

    <NxBanner v-else-if="error && !conversation" variant="danger" class="cx-assistant__banner">{{ error }}</NxBanner>

    <template v-else>
      <NxChatThread
        class="cx-assistant__thread"
        :stick-key="stickKey"
        :near-bottom-px="64"
      >
        <NxEmptyState v-if="messages.length === 0" compact :title="t('connect.assistant.empty')" />
        <NxChatMessage
          v-for="m in messages"
          :key="m.id"
          :tone="m.role === 'user' ? 'user' : 'agent'"
          compact
        >
          <template #meta>
            <span>{{ m.role === 'user' ? t('connect.assistant.you') : t('connect.assistant.bot') }}</span>
            <span v-if="m.pending" class="cx-assistant__pending">…</span>
          </template>
          <div class="cx-assistant__bubble" :class="{ pending: m.pending && !m.content }">
            <template v-if="m.content">{{ m.content }}</template>
            <template v-else-if="m.pending">{{ t('connect.assistant.thinking') }}</template>
          </div>
          <NxBanner v-if="m.error" variant="danger">{{ m.error }}</NxBanner>
        </NxChatMessage>
      </NxChatThread>

      <NxChatComposer
        v-model="draft"
        class="cx-assistant__composer"
        :placeholder="t('connect.assistant.placeholder')"
        :sending="sending"
        :cancellable="sending"
        :disabled="!conversation || booting"
        @send="onSend"
        @cancel="cancel"
      />
    </template>
  </NxFloatingPanel>
</template>

<style scoped>
.cx-assistant__spark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  flex-shrink: 0;
}

.cx-assistant__thread {
  flex: 1;
  min-height: 0;
  padding: calc(var(--nxt-space-xs) * 1.25) var(--nxt-space-sm);
}

.cx-assistant__bubble {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  white-space: pre-wrap;
  word-break: break-word;
}

.cx-assistant__bubble.pending {
  color: var(--nxt-fg-muted);
  font-style: italic;
}

.cx-assistant__pending {
  margin-left: var(--nxt-space-2xs);
  opacity: 0.7;
}

.cx-assistant__composer {
  flex-shrink: 0;
}

.cx-assistant__state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(var(--nxt-space-xs) * 1.25);
  padding: var(--nxt-space-lg);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
  text-align: center;
}
.cx-assistant__banner {
  flex-shrink: 0;
  margin: var(--nxt-space-xs) var(--nxt-space-sm) 0;
}
</style>
